import { useRef, useState } from "react";
import { Download, Trash2, Upload } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { api, API } from "@/lib/auth";

const isVideoUrl = (u) => /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(u || "");

/**
 * AdminVendorMediaManager — admin-only UI for a vendor's uploaded media.
 * - Thumbnails with click-to-download (streams file with attachment header)
 * - Delete individual entries (with confirm)
 * - Upload additional images/videos (appended to media_urls)
 *
 * Props:
 *  - appId: vendor application id
 *  - mediaUrls: string[]
 *  - orientation: "portrait" | "landscape" | null
 *  - onUpdated(newUrls): callback with the updated array after add/delete
 */
const AdminVendorMediaManager = ({
  appId,
  mediaUrls = [],
  orientation,
  onUpdated,
}) => {
  const [busyUrl, setBusyUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const aspect = orientation === "landscape" ? "aspect-[4/3]" : "aspect-[3/4]";

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const absoluteUrl = (u) =>
    /^https?:/i.test(u) ? u : `${backendBase}${u}`;

  const handleDownload = async (url) => {
    setBusyUrl(`download:${url}`);
    try {
      const r = await api.get(
        `/admin/vendor-applications/${appId}/media/download`,
        {
          params: { url },
          responseType: "blob",
        }
      );
      const blob = new Blob([r.data]);
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      const filename = (url.split("/").pop() || "media").split("?")[0];
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast(e?.response?.data?.detail || "Download failed", { duration: 2400 });
    } finally {
      setBusyUrl(null);
    }
  };

  const handleDelete = async (url) => {
    if (!window.confirm("Delete this media file? This cannot be undone.")) return;
    setBusyUrl(`delete:${url}`);
    try {
      const r = await api.delete(
        `/admin/vendor-applications/${appId}/media`,
        { params: { url } }
      );
      onUpdated?.(r.data?.media_urls || []);
      toast("Media deleted", { duration: 1800 });
    } catch (e) {
      toast(e?.response?.data?.detail || "Delete failed", { duration: 2400 });
    } finally {
      setBusyUrl(null);
    }
  };

  const handleAddFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(
        `${API}/admin/vendor-applications/${appId}/media`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      onUpdated?.(r.data?.media_urls || []);
      toast(`${files.length} file${files.length === 1 ? "" : "s"} added`, {
        duration: 1800,
      });
    } catch (err) {
      toast(err?.response?.data?.detail || "Upload failed", { duration: 2600 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="mt-6" data-testid="admin-vendor-media-manager">
      <div className="flex items-center justify-between mb-3">
        <p className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
          Portfolio media · {mediaUrls.length}{" "}
          {mediaUrls.length === 1 ? "file" : "files"}
          {orientation ? ` · ${orientation}` : ""}
        </p>
        <label className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors cursor-pointer">
          <Upload size={12} strokeWidth={1.7} />
          {uploading ? "Uploading…" : "Add"}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleAddFiles}
            disabled={uploading}
            data-testid="admin-vendor-media-add-input"
            className="hidden"
          />
        </label>
      </div>

      {mediaUrls.length === 0 ? (
        <p className="text-[12px] text-[var(--ld-ink-muted)]">
          No media uploaded yet.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {mediaUrls.map((u) => {
            const vid = isVideoUrl(u);
            const src = absoluteUrl(u);
            return (
              <div
                key={u}
                data-testid={`admin-vendor-media-item-${u}`}
                className={`relative ${aspect} overflow-hidden border border-[var(--ld-ink)]/15 bg-[var(--ld-ink)]/5 group`}
              >
                {vid ? (
                  <video
                    src={src}
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img src={src} alt="" className="h-full w-full object-cover" />
                )}
                <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-[var(--ld-ink)]/85 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => handleDownload(u)}
                    disabled={busyUrl === `download:${u}`}
                    data-testid={`admin-vendor-media-download-${u}`}
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-[var(--ld-gold)] hover:text-[var(--ld-ivory)] disabled:opacity-60 transition-colors"
                    aria-label="Download"
                  >
                    <Download size={12} strokeWidth={1.7} />
                    {busyUrl === `download:${u}` ? "…" : "DL"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(u)}
                    disabled={busyUrl === `delete:${u}`}
                    data-testid={`admin-vendor-media-delete-${u}`}
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-red-300 hover:text-red-100 disabled:opacity-60 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 size={12} strokeWidth={1.7} />
                    {busyUrl === `delete:${u}` ? "…" : "DEL"}
                  </button>
                </div>
                {vid && (
                  <span className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase bg-[var(--ld-ink)]/85 text-[var(--ld-gold)]">
                    Video
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminVendorMediaManager;
